<template>
  <div class="visual-editor-container">
    <!-- === ОБЩИЕ НАСТРОЙКИ === -->
    <div
      v-if="!['spacer', 'header', 'footer'].includes(blockType)"
      class="settings-card mb-4"
    >
      <div class="card-header">
        <span class="card-title">Основные настройки</span>
      </div>
      <div class="card-body">
        <div class="common-settings-grid">
          <div class="common-setting-item">
            <base-label>Заголовок</base-label>
            <input
              type="text"
              v-model="localConfig.label"
              ref="blockLabel"
              class="default-input w-full"
              @focus="setCommonFocus('label', 'blockLabel')"
            />
          </div>
          <div class="common-setting-item">
            <div class="checkbox-wrapper">
              <!-- Нумерация -->
              <div>
                <base-checkbox
                  v-model="localConfig.sequence"
                  class="checkbox-item"
                >
                  Нумерация
                </base-checkbox>
              </div>
              <!-- Скрыть шапку таблицы -->
              <div
                v-if="
                  [
                    'table',
                    'items_table',
                    'sectioned_items_table',
                  ].includes(blockType)
                "
                class="checkbox-item"
              >
                <base-checkbox v-model="localConfig.hide_headers">
                  Скрыть шапку таблицы
                </base-checkbox>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 1. SPACER -->
    <div v-if="blockType === 'spacer'" class="settings-card">
      <div class="card-header">
        <span class="card-title">Настройки отступа</span>
      </div>
      <div class="card-body">
        <div class="spacer-control">
          <div class="spacer-label">
            <base-label>
              Высота: {{ localConfig.height }}px
            </base-label>
          </div>
          <div class="spacer-slider-container">
            <input
              type="range"
              min="0"
              max="1000"
              v-model.number="localConfig.height"
              class="range-slider"
            />
            <div class="spacer-input-wrapper">
              <input
                type="number"
                v-model.number="localConfig.height"
                class="default-input h-9 w-20 text-center"
                min="0"
                max="1000"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 2. TEXT -->
    <div v-else-if="blockType === 'text'" class="settings-card">
      <div class="card-header">
        <span class="card-title">Содержимое</span>
      </div>
      <div class="card-body">
        <div class="textarea-container">
          <base-label>Текст документа</base-label>
          <textarea
            ref="textEditor"
            v-model="localConfig.text"
            class="visual-textarea"
            @focus="setCommonFocus('text', 'textEditor')"
          ></textarea>
        </div>
      </div>
    </div>

    <!-- 3. УНИВЕРСАЛЬНЫЙ БЛОК ТАБЛИЦ -->
    <div
      v-else-if="
        ['table', 'items_table', 'sectioned_items_table'].includes(
          blockType
        )
      "
      class="settings-card"
    >
      <!-- ЕДИНЫЙ ХЕДЕР ТАБЛИЦЫ -->
      <div class="card-header flex justify-between items-center">
        <span class="card-title">Редактор таблицы</span>
      </div>

      <!-- Доп. настройки для items_table -->
      <div
        v-if="blockType === 'items_table'"
        class="table-settings-section sectioned-settings"
      >
        <div class="setting-row">
          <div class="setting-label">
            <base-label>Источник данных</base-label>
          </div>
          <div class="setting-control">
            <base-select
              v-model="localConfig.data_source"
              :options="dataSourceOptions"
              :clearable="false"
            />
          </div>
        </div>
      </div>

      <!-- Доп. настройки для sectioned_items_table -->
      <div
        v-if="blockType === 'sectioned_items_table'"
        class="table-settings-section sectioned-settings"
      >
        <div class="setting-row">
          <div class="setting-label">
            <base-label>Заголовок секции</base-label>
          </div>
          <div class="setting-control">
            <input
              type="text"
              ref="sectionLabelEditor"
              v-model="localConfig.section_label"
              class="default-input w-full"
              @focus="
                setCommonFocus('section_label', 'sectionLabelEditor')
              "
            />
          </div>
        </div>

        <div class="setting-row">
          <div class="setting-label">
            <base-label>Источник данных</base-label>
          </div>
          <div class="setting-control">
            <base-select
              v-model="localConfig.data_source"
              :options="dataSourceOptions"
              :clearable="false"
            />
          </div>
        </div>

        <div class="checkbox-group">
          <base-checkbox
            v-model="localConfig.number_section"
            class="checkbox-item"
          >
            Нумерация секций
          </base-checkbox>
          <base-checkbox
            v-model="localConfig.number_cells"
            class="checkbox-item"
          >
            Нумерация строк
          </base-checkbox>
        </div>
      </div>

      <div class="settings-actions">
        <button class="btn-text-action" @click="addColTable">
          <span class="btn-icon">+</span>
          Добавить столбец
        </button>
      </div>

      <!-- ЕДИНОЕ ТЕЛО ТАБЛИЦЫ -->
      <div class="card-body p-0">
        <div class="grid-table-scroll">
          <div class="grid-table-container">
            <div
              class="grid-row header-row"
              :style="getGridStyle(localConfig.headers)"
              :class="{ 'headers-hidden': localConfig.hide_headers }"
            >
              <div
                v-for="(header, hIdx) in localConfig.headers"
                :key="'h-' + hIdx"
                class="grid-cell header-cell group"
              >
                <!-- Показываем либо инпут, либо сообщение о скрытой шапке -->
                <template v-if="!localConfig.hide_headers">
                  <input
                    v-model="localConfig.headers[hIdx]"
                    class="header-input"
                    :ref="(el) => setHeaderRef(el, hIdx)"
                    @focus="setHeaderFocus(hIdx)"
                  />
                  <!-- Скрываем кнопку удаления если остался 1 столбец -->
                  <button
                    v-if="
                      localConfig.headers &&
                      localConfig.headers.length > 1
                    "
                    class="remove-col-btn group-hover:opacity-100"
                    @click="removeCol(hIdx)"
                    title="Удалить столбец"
                  >
                    ×
                  </button>
                </template>
                <template v-else>
                  <div class="hidden-header-info">
                    <span class="hidden-header-text">
                      Шапка скрыта
                    </span>
                    <!-- Скрываем кнопку удаления если остался 1 столбец -->
                    <button
                      v-if="
                        localConfig.headers &&
                        localConfig.headers.length > 1
                      "
                      class="remove-col-btn"
                      @click="removeCol(hIdx)"
                      title="Удалить столбец"
                    >
                      ×
                    </button>
                  </div>
                </template>
              </div>
              <div
                v-if="blockType !== 'items_table'"
                class="grid-cell header-cell empty-corner"
              ></div>
            </div>

            <!-- 2. СТРОКИ -->
            <div
              v-for="(row, rIdx) in localConfig.rows"
              :key="'r-' + rIdx"
              class="grid-row body-row"
              :style="getGridStyle(localConfig.headers)"
            >
              <div
                v-for="(cell, cIdx) in row"
                :key="'c-' + cIdx"
                class="grid-cell body-cell"
              >
                <input
                  :ref="(el) => setCellRef(el, rIdx, cIdx)"
                  v-model="localConfig.rows[rIdx][cIdx]"
                  :class="[
                    'cell-input',
                    blockType !== 'table' ? 'code-input-style' : '',
                  ]"
                  :placeholder="''"
                  @focus="setTableFocus(rIdx, cIdx)"
                />
              </div>

              <!-- КНОПКА УДАЛЕНИЯ СТРОКИ -->
              <div
                v-if="blockType !== 'items_table'"
                class="grid-cell action-cell"
              >
                <button
                  class="remove-row-btn"
                  @click="removeRowTable(rIdx)"
                  title="Удалить строку"
                >
                  ×
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- КНОПКА ДОБАВЛЕНИЯ СТРОКИ -->
        <button
          v-if="blockType !== 'items_table'"
          class="btn-block-add"
          @click="addRowTable"
        >
          <span class="btn-icon">+</span>
          Добавить строку
        </button>

        <!-- Сообщение для items_table -->
        <div v-else class="items-table-info">
          <div class="info-icon">ℹ️</div>
          <p>
            Эта таблица будет автоматически заполняться данными из
            источника "{{ localConfig.data_source || "..." }}"
          </p>
          <p class="info-sub">
            Каждая строка представляет собой шаблон для одного
            элемента данных
          </p>
        </div>
      </div>
    </div>

    <!-- 4. SIDE_BY_SIDE -->
    <div
      v-else-if="blockType === 'side_by_side'"
      class="settings-card"
    >
      <div class="card-header">
        <span class="card-title">Блок Side by Side</span>
      </div>
      <div class="card-body">
        <div
          v-if="localConfig.blocks && localConfig.blocks.length >= 2"
          class="side-by-side-container"
        >
          <!-- Левый -->
          <div class="sbs-column">
            <div class="sbs-header">
              <base-label>Левый блок</base-label>
            </div>
            <div class="sbs-body">
              <div class="sbs-field-group">
                <base-label class="mb-1">Заголовок</base-label>
                <input
                  type="text"
                  v-model="localConfig.blocks[0].label"
                  ref="sbsLabel0"
                  class="default-input mb-4 w-full"
                  @focus="setSbsBlockFocus(0)"
                />
              </div>
              <div class="sbs-field-group">
                <base-label class="mb-2">Поля</base-label>
                <div class="field-list-wrapper">
                  <div
                    v-for="(field, fIdx) in localConfig.blocks[0]
                      .fields"
                    :key="'l-' + fIdx"
                    class="mini-field-row"
                  >
                    <div class="field-label-input">
                      <input
                        v-model="field.label"
                        placeholder="Заголовок поля"
                        class="mini-input label-input"
                        :ref="(el) => setSbsLabelRef(el, 0, fIdx)"
                        @focus="setSbsLabelFocus(0, fIdx)"
                      />
                    </div>
                    <div class="field-value-input">
                      <input
                        type="text"
                        v-model="field.value"
                        :ref="(el) => setSideValueRef(el, 0, fIdx)"
                        @focus="setSideValueFocus(0, fIdx)"
                        class="default-input mini-var-input w-full"
                      />
                    </div>
                    <div class="field-action">
                      <button
                        class="mini-del-btn"
                        @click="removeSideField(0, fIdx)"
                        title="Удалить поле"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                  <button
                    class="mini-add-btn"
                    @click="addSideField(0)"
                  >
                    <span class="btn-icon">+</span>
                    Добавить поле
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="sbs-divider">
            <div class="divider-line"></div>
          </div>

          <!-- Правый -->
          <div class="sbs-column">
            <div class="sbs-header">
              <base-label>Правый блок</base-label>
            </div>
            <div class="sbs-body">
              <div class="sbs-field-group">
                <base-label class="mb-1">Заголовок</base-label>
                <input
                  type="text"
                  v-model="localConfig.blocks[1].label"
                  ref="sbsLabel1"
                  class="default-input mb-4 w-full"
                  @focus="setSbsBlockFocus(1)"
                />
              </div>
              <div class="sbs-field-group">
                <base-label class="mb-2">Поля</base-label>
                <div class="field-list-wrapper">
                  <div
                    v-for="(field, fIdx) in localConfig.blocks[1]
                      .fields"
                    :key="'r-' + fIdx"
                    class="mini-field-row"
                  >
                    <div class="field-label-input">
                      <input
                        v-model="field.label"
                        placeholder="Заголовок поля"
                        class="mini-input label-input"
                        :ref="(el) => setSbsLabelRef(el, 1, fIdx)"
                        @focus="setSbsLabelFocus(1, fIdx)"
                      />
                    </div>
                    <div class="field-value-input">
                      <input
                        type="text"
                        v-model="field.value"
                        :ref="(el) => setSideValueRef(el, 1, fIdx)"
                        @focus="setSideValueFocus(1, fIdx)"
                        class="default-input mini-var-input w-full"
                      />
                    </div>
                    <div class="field-action">
                      <button
                        class="mini-del-btn"
                        @click="removeSideField(1, fIdx)"
                        title="Удалить поле"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                  <button
                    class="mini-add-btn"
                    @click="addSideField(1)"
                  >
                    <span class="btn-icon">+</span>
                    Добавить поле
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="error-banner">
          ⚠️ Ошибка структуры SideBySide. Сбросьте конфиг.
        </div>
      </div>
    </div>

    <!-- 5. SIGNATURE -->
    <div v-else-if="blockType === 'signature'" class="settings-card">
      <div class="card-header flex justify-between items-center">
        <span class="card-title">Блок подписей</span>
        <div class="header-checkbox">
          <base-checkbox v-model="localConfig.show_lines">
            Показывать линии для подписи
          </base-checkbox>
        </div>
      </div>
      <div class="card-body">
        <div class="signatures-grid">
          <div
            v-for="(field, idx) in localConfig.fields || []"
            :key="idx"
            class="signature-item"
          >
            <div class="sig-inputs">
              <div class="sig-field-group">
                <base-label class="text-xs mb-1">
                  Роль / Заголовок
                </base-label>
                <input
                  type="text"
                  v-model="field.label"
                  :ref="(el) => setSigLabelRef(el, idx)"
                  class="default-input w-full"
                  @focus="setSigLabelFocus(idx)"
                />
              </div>
              <div class="sig-field-group mt-3">
                <base-label class="text-xs mb-1">
                  ФИО / Значение
                </base-label>
                <input
                  type="text"
                  v-model="field.value"
                  :ref="(el) => setSigValueRef(el, idx)"
                  @focus="setSigValueFocus(idx)"
                  class="default-input w-full"
                />
              </div>
            </div>
            <!-- Кнопка удаления показывается только если 3 элемента и больше -->
            <button
              v-if="
                localConfig.fields && localConfig.fields.length >= 3
              "
              class="sig-remove"
              @click="removeSigField(idx)"
            >
              Удалить
            </button>
          </div>
          <button class="signature-add-card" @click="addSigField">
            <span class="plus-icon">+</span>
            <span>Добавить подписанта</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 6. HEADER -->
    <div v-else-if="blockType === 'header'" class="settings-card">
      <div class="card-header">
        <span class="card-title">Содержимое</span>
      </div>
      <div class="card-body">
        <div class="textarea-container">
          <base-label>Текст колонтитула</base-label>
          <textarea
            ref="textEditor"
            v-model="localConfig.text"
            class="visual-textarea"
            @focus="setCommonFocus('text', 'textEditor')"
          ></textarea>
        </div>
      </div>
    </div>

    <!-- 7. FOOTER -->
    <div v-else-if="blockType === 'footer'" class="settings-card">
      <div class="card-header">
        <span class="card-title">Содержимое</span>
      </div>
      <div class="card-body">
        <div class="textarea-container">
          <base-label>Текст колонтитула</base-label>
          <textarea
            ref="textEditor"
            v-model="localConfig.text"
            class="visual-textarea"
            @focus="setCommonFocus('text', 'textEditor')"
          ></textarea>
        </div>
      </div>
    </div>

    <!-- Пустой -->
    <div v-else class="empty-state">
      <div class="empty-icon">⚙️</div>
      <p>Выберите тип блока для настройки</p>
    </div>

    <!-- Компонент Стилей -->
    <TemplateStyleEditor
      v-if="blockType && blockType !== 'spacer'"
      v-model="localConfig.style"
      :block-type="blockType"
      :template-styles="templateStyles"
    />
  </div>
</template>

<script>
import { DEFAULT_BLOCK_CONFIGS } from "@/config/template-blocks/blockConfigs";

import TemplateStyleEditor from "./TemplateStyleEditor.vue";

export default {
  name: "TemplateBlockVisualEditor",
  props: {
    modelValue: { type: Object, default: () => ({}) },
    blockType: { type: String, default: "" },
    templateStyles: {
      type: Object,
      default: () => ({}),
    },
  },
  components: { TemplateStyleEditor },
  emits: ["update:modelValue"],
  computed: {
    shouldShowHeaders() {
      if (this.blockType === "sectioned_items_table") {
        return (
          this.localConfig.headers &&
          this.localConfig.headers.length > 0
        );
      }
      return true;
    },
    canRemoveColumns() {
      return (
        this.localConfig.headers &&
        this.localConfig.headers.length > 1
      );
    },
  },
  data() {
    return {
      localConfig: this.initializeConfig(
        this.modelValue,
        this.blockType
      ),
      focusContext: null,
      cellRefs: {},
      sideValueRefs: {},
      sideLabelRefs: {},
      sigValueRefs: {},
      sigLabelRefs: {},
      headerRefs: {},
      dataSourceOptions: [
        {
          value: "trip_vehicles",
          label: "Транспорт (trip_vehicles)",
        },
        { value: "trip_drivers", label: "Водители (trip_drivers)" },
        {
          value: "order_points",
          label: "Точки заказа (order_points)",
        },
      ],
    };
  },
  watch: {
    modelValue: {
      handler(newVal) {
        const initialized = this.initializeConfig(
          newVal,
          this.blockType
        );
        if (
          JSON.stringify(initialized) !==
          JSON.stringify(this.localConfig)
        ) {
          this.localConfig = initialized;
        }
      },
      deep: true,
    },

    localConfig: {
      handler(newVal) {
        this.$emit("update:modelValue", newVal);
      },
      deep: true,
    },

    blockType(newType, oldType) {
      if (newType !== oldType) {
        this.localConfig = this.getDefaultConfig(newType);
      }
      this.cellRefs = {};
      this.sideValueRefs = {};
      this.sideLabelRefs = {};
      this.sigValueRefs = {};
      this.sigLabelRefs = {};
      this.headerRefs = {};
    },

    "localConfig.data_source": {
      handler(newSource, oldSource) {
        if (
          !["items_table", "sectioned_items_table"].includes(
            this.blockType
          )
        ) {
          return;
        }

        if (newSource && newSource !== oldSource) {
          const defaultForSource =
            DEFAULT_BLOCK_CONFIGS[this.blockType]?.[newSource];

          if (defaultForSource) {
            // Сохраняем только data_source и обновляем остальное
            const updatedConfig = {
              ...defaultForSource,
              data_source: newSource,
            };

            Object.assign(this.localConfig, updatedConfig);
          }
        }
      },
    },
  },
  methods: {
    // Получение конфигурации по умолчанию
    getDefaultConfig(type) {
      const defaultConfig = DEFAULT_BLOCK_CONFIGS[type] || {};

      if (
        type === "items_table" ||
        type === "sectioned_items_table"
      ) {
        const firstSource = "trip_vehicles";
        return JSON.parse(
          JSON.stringify(defaultConfig[firstSource] || {})
        );
      }

      if (type === "sectioned_items_table") {
        const config = JSON.parse(JSON.stringify(defaultConfig));
        if (
          config.rows &&
          config.rows.length > 0 &&
          (!config.headers || config.headers.length === 0)
        ) {
          const firstRow = config.rows[0];
          const colCount = firstRow.length;
          config.headers = Array(colCount)
            .fill("")
            .map((_, i) => `Колонка ${i + 1}`);
        }
        if (typeof config.hide_headers === "undefined") {
          config.hide_headers = false;
        }
        return config;
      }

      return JSON.parse(JSON.stringify(defaultConfig));
    },

    initializeConfig(config, type) {
      const defaults = this.getDefaultConfig(type);

      if (!config || Object.keys(config).length === 0) {
        return defaults;
      }

      const result = { ...defaults, ...config };

      if (
        ["table", "items_table", "sectioned_items_table"].includes(
          type
        )
      ) {
        if (type === "items_table") {
          if (result.rows && Array.isArray(result.rows)) {
            if (
              result.rows.length > 0 &&
              typeof result.rows[0] === "string"
            ) {
              result.rows = [result.rows];
            }
          }
        }

        if (
          type === "sectioned_items_table" &&
          result.rows &&
          Array.isArray(result.rows)
        ) {
          if (
            result.rows.length > 0 &&
            typeof result.rows[0] === "string"
          ) {
            result.rows = [result.rows];
          }
        }

        if (!result.rows || !Array.isArray(result.rows)) {
          result.rows = defaults.rows ? [...defaults.rows] : [[]];
        } else {
          result.rows = result.rows.map((row) => {
            if (typeof row === "string") return [row];
            if (Array.isArray(row)) return row;
            return [];
          });
        }

        if (!result.headers || !Array.isArray(result.headers)) {
          if (
            result.rows &&
            result.rows.length > 0 &&
            result.rows[0]
          ) {
            const firstRow = result.rows[0];
            const colCount = firstRow.length;
            result.headers = Array(colCount)
              .fill("")
              .map((_, i) => `Колонка ${i + 1}`);
          } else {
            result.headers = defaults.headers
              ? [...defaults.headers]
              : [];
          }
        }

        const hLen = result.headers.length;
        if (hLen > 0) {
          result.rows.forEach((r) => {
            if (!Array.isArray(r)) r = [];
            while (r.length < hLen) r.push("");
            if (r.length > hLen) r.splice(hLen);
          });
        } else if (result.rows && result.rows.length > 0) {
          const firstRow = result.rows[0];
          const colCount = firstRow ? firstRow.length : 0;
          result.headers = Array(colCount)
            .fill("")
            .map((_, i) => `Колонка ${i + 1}`);

          result.rows.forEach((r) => {
            if (!Array.isArray(r)) r = [];
            while (r.length < colCount) r.push("");
            if (r.length > colCount) r.splice(colCount);
          });
        }

        if (typeof result.hide_headers === "undefined") {
          result.hide_headers = type === "sectioned_items_table";
        }

        if (type !== "table" && !result.data_source) {
          result.data_source =
            defaults.data_source || "trip_vehicles";
        }
      }

      return result;
    },

    getGridStyle(headers) {
      const count = headers ? headers.length : 1;
      if (this.blockType === "items_table") {
        return {
          gridTemplateColumns: `repeat(${count}, minmax(150px, 1fr))`,
        };
      }
      return {
        gridTemplateColumns: `repeat(${count}, minmax(150px, 1fr)) 40px`,
      };
    },

    // --- ФОКУС ---
    setCommonFocus(prop, refName) {
      this.focusContext = { type: "common", prop, refName };
    },
    setHeaderFocus(idx) {
      this.focusContext = { type: "header", idx };
    },
    setTableFocus(row, col) {
      this.focusContext = { type: "table", row, col };
    },
    setSbsBlockFocus(blockIdx) {
      this.focusContext = {
        type: "sbs_block_label",
        blockIdx,
        refName: `sbsLabel${blockIdx}`,
      };
    },
    setSbsLabelFocus(blockIdx, fieldIdx) {
      this.focusContext = {
        type: "sbs_field_label",
        blockIdx,
        fieldIdx,
      };
    },
    setSideValueFocus(b, f) {
      this.focusContext = {
        type: "sbs_field_value",
        blockIdx: b,
        fieldIdx: f,
      };
    },
    setSigLabelFocus(idx) {
      this.focusContext = { type: "sig_label", idx };
    },
    setSigValueFocus(idx) {
      this.focusContext = { type: "sig_value", idx };
    },

    setCellRef(el, r, c) {
      if (el) this.cellRefs[`${r}-${c}`] = el;
    },
    setHeaderRef(el, idx) {
      if (el) this.headerRefs[idx] = el;
    },
    setSideValueRef(el, b, f) {
      if (el) this.sideValueRefs[`${b}-${f}`] = el;
    },
    setSbsLabelRef(el, b, f) {
      if (el) this.sideLabelRefs[`${b}-${f}`] = el;
    },
    setSigValueRef(el, i) {
      if (el) this.sigValueRefs[i] = el;
    },
    setSigLabelRef(el, i) {
      if (el) this.sigLabelRefs[i] = el;
    },

    addColTable() {
      if (
        !this.localConfig.headers ||
        !Array.isArray(this.localConfig.headers)
      ) {
        this.localConfig.headers = [];
      }
      if (
        !this.localConfig.rows ||
        !Array.isArray(this.localConfig.rows) ||
        this.localConfig.rows.length === 0
      ) {
        this.localConfig.rows =
          this.blockType === "items_table" ? [[]] : [[]];
      }

      this.localConfig.headers.push("");

      if (this.blockType === "items_table") {
        if (
          this.localConfig.rows[0] &&
          Array.isArray(this.localConfig.rows[0])
        ) {
          this.localConfig.rows[0].push("");
        } else {
          this.localConfig.rows[0] = [""];
        }
      } else {
        this.localConfig.rows.forEach((r) => {
          if (Array.isArray(r)) {
            r.push("");
          }
        });
      }
    },

    removeCol(idx) {
      if (
        this.localConfig.headers &&
        this.localConfig.headers.length <= 1
      ) {
        return;
      }
      if (
        this.localConfig.headers &&
        this.localConfig.headers.length > idx
      ) {
        this.localConfig.headers.splice(idx, 1);
      }
      if (this.localConfig.rows) {
        this.localConfig.rows.forEach((r) => {
          if (Array.isArray(r) && r.length > idx) {
            r.splice(idx, 1);
          }
        });
      }
    },

    addRowTable() {
      if (this.blockType === "items_table") return;

      if (
        !this.localConfig.rows ||
        !Array.isArray(this.localConfig.rows)
      ) {
        this.localConfig.rows = [];
      }

      let len = 0;
      if (
        this.localConfig.headers &&
        Array.isArray(this.localConfig.headers)
      ) {
        len = this.localConfig.headers.length;
      } else if (
        this.localConfig.rows[0] &&
        Array.isArray(this.localConfig.rows[0])
      ) {
        len = this.localConfig.rows[0].length;
      }

      this.localConfig.rows.push(new Array(len).fill(""));
    },

    removeRowTable(idx) {
      if (this.blockType === "items_table") return;
      if (
        this.localConfig.rows &&
        Array.isArray(this.localConfig.rows) &&
        this.localConfig.rows.length > idx
      ) {
        this.localConfig.rows.splice(idx, 1);
      }
    },

    addSideField(b) {
      if (!this.localConfig.blocks[b].fields) {
        this.localConfig.blocks[b].fields = [];
      }
      this.localConfig.blocks[b].fields.push({
        label: "",
        value: "",
      });
    },
    removeSideField(b, f) {
      this.localConfig.blocks[b].fields.splice(f, 1);
    },
    addSigField() {
      if (!this.localConfig.fields) {
        this.localConfig.fields = [];
      }
      this.localConfig.fields.push({ label: "", value: "" });
    },
    removeSigField(i) {
      if (
        this.localConfig.fields &&
        this.localConfig.fields.length > 0
      ) {
        this.localConfig.fields.splice(i, 1);
      }
    },

    insertVariable(payload) {
      const code =
        typeof payload === "object" ? payload.code : String(payload);
      const textToInsert = code.startsWith("{{")
        ? code
        : `{{ ${code} }}`;

      if (!this.focusContext) return;
      const ctx = this.focusContext;
      let inputEl = null;

      // Получаем прямой доступ к DOM элементу input/textarea
      if (ctx.type === "common") {
        inputEl = this.$refs[ctx.refName];
      } else if (ctx.type === "header") {
        inputEl = this.headerRefs[ctx.idx];
      } else if (ctx.type === "table") {
        inputEl = this.cellRefs[`${ctx.row}-${ctx.col}`];
      } else if (ctx.type === "sbs_block_label") {
        inputEl = this.$refs[ctx.refName];
      } else if (ctx.type === "sbs_field_label") {
        inputEl =
          this.sideLabelRefs[`${ctx.blockIdx}-${ctx.fieldIdx}`];
      } else if (ctx.type === "sbs_field_value") {
        inputEl =
          this.sideValueRefs[`${ctx.blockIdx}-${ctx.fieldIdx}`];
      } else if (ctx.type === "sig_label") {
        inputEl = this.sigLabelRefs[ctx.idx];
      } else if (ctx.type === "sig_value") {
        inputEl = this.sigValueRefs[ctx.idx];
      }

      // Вставка текста
      if (
        inputEl &&
        (inputEl.tagName === "INPUT" ||
          inputEl.tagName === "TEXTAREA")
      ) {
        inputEl.focus();

        inputEl.setRangeText(
          textToInsert,
          inputEl.selectionStart,
          inputEl.selectionEnd,
          "end"
        );

        // Принудительно вызываем событие input для обновления v-model
        inputEl.dispatchEvent(new Event("input", { bubbles: true }));
      }
    },

    validateStructure(type) {
      if (type === "sectioned_items_table") {
        if (
          !this.localConfig.rows ||
          this.localConfig.rows.length === 0
        ) {
          const defaults = this.getDefaultConfig(type);
          this.localConfig.rows = defaults.rows
            ? [...defaults.rows]
            : [[]];
        }

        if (
          (!this.localConfig.headers ||
            this.localConfig.headers.length === 0) &&
          this.localConfig.rows &&
          this.localConfig.rows.length > 0
        ) {
          const firstRow = this.localConfig.rows[0];
          const colCount = firstRow.length;
          this.localConfig.headers = Array(colCount)
            .fill("")
            .map((_, i) => `Колонка ${i + 1}`);
        }

        if (typeof this.localConfig.hide_headers === "undefined") {
          this.localConfig.hide_headers = true;
        }

        if (!this.localConfig.data_source) {
          this.localConfig.data_source = "order_points";
        }
      }

      if (["table", "items_table"].includes(type)) {
        if (
          !this.localConfig.headers ||
          this.localConfig.headers.length === 0
        ) {
          const defaults = this.getDefaultConfig(type);
          this.localConfig.headers = defaults.headers
            ? [...defaults.headers]
            : [];
        }

        if (
          !this.localConfig.rows ||
          this.localConfig.rows.length === 0
        ) {
          const defaults = this.getDefaultConfig(type);
          this.localConfig.rows = defaults.rows
            ? [...defaults.rows]
            : [[]];
        }
      }

      if (
        type === "signature" &&
        (!this.localConfig.fields ||
          this.localConfig.fields.length === 0)
      ) {
        const defaults = this.getDefaultConfig(type);
        this.localConfig.fields = defaults.fields
          ? [...defaults.fields]
          : [];
      }
    },
  },
  mounted() {
    this.validateStructure(this.blockType);
  },
};
</script>

<style scoped>
.visual-editor-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: "Inter", sans-serif;
}

.default-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.5;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  background-color: #fff;
  color: #1f2937;
}

.default-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.default-input::placeholder {
  color: #9ca3af;
}

/* === CARDS === */
.settings-card {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}
.card-header {
  background: #f0f0f0;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.card-title {
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.card-body {
  padding: 16px;
}
.card-body.p-0 {
  padding: 0;
}

.common-settings-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}
.common-setting-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.checkbox-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.checkbox-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.settings-actions {
  display: flex;
  justify-content: flex-end;
  padding: 12px 16px;
}

/* === SPACER === */
.spacer-control {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.spacer-slider-container {
  display: flex;
  align-items: center;
  gap: 16px;
}
.range-slider {
  flex: 1;
  height: 6px;
  -webkit-appearance: none;
  appearance: none;
  background: #e5e7eb;
  border-radius: 3px;
  outline: none;
}
.range-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  background: #3b82f6;
  border-radius: 50%;
  cursor: pointer;
}
.range-slider::-moz-range-thumb {
  width: 18px;
  height: 18px;
  background: #3b82f6;
  border-radius: 50%;
  cursor: pointer;
  border: none;
}
.spacer-input-wrapper {
  flex-shrink: 0;
}

/* === TEXT === */
.textarea-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.visual-textarea {
  width: 100%;
  min-height: 150px;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-family: "Menlo", "Monaco", "Courier New", monospace;
  font-size: 13px;
  line-height: 1.5;
  resize: vertical;
  transition: border-color 0.2s;
}
.visual-textarea:focus {
  border-color: var(--color-green);
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* === TABLE SETTINGS === */
.table-settings-section {
  padding: 12px 16px 0 16px;
  background: #f0f0f0;
}
.sectioned-settings {
  background: #fff;
}
.setting-row {
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 16px;
  align-items: center;
  margin-bottom: 12px;
}
.setting-label {
  font-size: 13px;
  font-weight: 500;
}
.setting-control {
  width: 100%;
}
.checkbox-group {
  display: flex;
  gap: 24px;
  margin-top: 16px;
}

/* === GRID TABLE SYSTEM === */
.grid-table-scroll {
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  margin: 0 16px;
}
.grid-table-container {
  display: flex;
  flex-direction: column;
  min-width: 100%;
  width: fit-content;
  background: #fff;
}

.grid-row {
  display: grid;
  border-bottom: 1px solid #e5e7eb;
}
.grid-row:last-child {
  border-bottom: none;
}
.header-row {
  background: #f8fafc;
  border-bottom: 2px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 10;
}
.header-row.headers-hidden {
  background: #f3f4f6;
}
.body-row:hover {
  background: #f9fafb;
}

.grid-cell {
  position: relative;
  border-right: 1px solid #e5e7eb;
  min-height: 44px;
  display: flex;
  align-items: stretch;
}
.grid-cell:last-child {
  border-right: none;
}
.header-cell {
  background: #f8fafc;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: #4b5563;
  padding: 0;
}
.header-row.headers-hidden .header-cell {
  background: #f3f4f6;
}
.empty-corner {
  background: #f8fafc;
}
.header-row.headers-hidden .empty-corner {
  background: #f3f4f6;
}
.action-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f9fafb;
  border-left: 1px solid #e5e7eb;
}

.header-cell input {
  width: 100%;
  background: transparent;
  border: none;
  outline: none;
  font-weight: 600;
  padding: 12px 8px;
  font-size: 12px;
}
.header-input {
  height: 100%;
  padding: 0 8px;
}

/* Контейнер для информации о скрытой шапке */
.hidden-header-info {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 8px;
}
.hidden-header-text {
  font-size: 11px;
  font-weight: 500;
  color: #6b7280;
  font-style: italic;
  opacity: 0.8;
}

.cell-input {
  width: 100%;
  border: none;
  padding: 12px;
  outline: none;
  font-size: 13px;
  background: transparent;
  height: 100%;
}
.cell-input:focus {
  background: #fff;
  box-shadow: inset 0 0 0 2px #3b82f6;
  z-index: 2;
}
.code-input-style {
  font-family: "Menlo", "Monaco", "Courier New", monospace;
  font-size: 12px;
}

.remove-col-btn {
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  color: #ef4444;
  background: #fee2e2;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  opacity: 0;
  font-size: 16px;
  line-height: 1;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}
.remove-col-btn:hover {
  background: #fecaca;
  opacity: 1 !important;
}
.group:hover .remove-col-btn {
  opacity: 0.6;
}
/* Для скрытой шапки кнопки всегда видны */
.header-row.headers-hidden .remove-col-btn {
  opacity: 0.6;
  position: relative;
  right: 0;
  top: 0;
  transform: none;
  align-self: center;
}
.header-row.headers-hidden .remove-col-btn:hover {
  opacity: 1;
}
/* Скрываем кнопку если остался 1 столбец */
.header-row .remove-col-btn.hidden,
.header-row.headers-hidden .remove-col-btn.hidden {
  display: none;
}

.remove-row-btn {
  width: 28px;
  height: 28px;
  border: 1px solid #fecaca;
  background: #fff;
  color: #ef4444;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  line-height: 1;
  transition: all 0.2s;
}
.remove-row-btn:hover {
  background: #fee2e2;
  border-color: #ef4444;
}

.btn-block-add {
  width: 100%;
  padding: 12px 16px;
  background: #f8fafc;
  border: none;
  border-top: 1px solid #e8e8e8;
  color: #2563eb;
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}
.btn-block-add:hover {
  background: #eff6ff;
}

.btn-text-action {
  background: #3b82f6;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  gap: 4px;
}
.btn-text-action:hover {
  background: #2563eb;
}
.btn-icon {
  font-size: 16px;
  font-weight: 400;
}

/* === ITEMS TABLE INFO === */
.items-table-info {
  width: 100%;
  padding: 12px 16px;
  background: #f0f9ff;
  border-top: 1px solid #e8e8e8;
  color: #0369a1;
  font-size: 13px;
  text-align: center;
}

.info-icon {
  font-size: 20px;
  margin-bottom: 8px;
}

.info-sub {
  font-size: 12px;
  color: #475569;
  margin-top: 4px;
  font-style: italic;
}

/* === SIDE BY SIDE === */
.side-by-side-container {
  display: flex;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}
.sbs-column {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.sbs-header {
  background: #f8fafc;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
}
.sbs-body {
  padding: 16px;
  flex: 1;
}
.sbs-field-group {
  margin-bottom: 16px;
}
.sbs-divider {
  width: 1px;
  background: #e5e7eb;
  position: relative;
}
.divider-line {
  position: absolute;
  top: 16px;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  width: 1px;
  background: #e5e7eb;
}
.field-list-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.mini-field-row {
  display: flex;
  gap: 8px;
  align-items: center;
}
.field-label-input {
  flex: 1;
}
.field-value-input {
  flex: 2;
}
.field-action {
  flex-shrink: 0;
}
.mini-input {
  width: 100%;
  padding: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  font-size: 12px;
  transition: border-color 0.2s;
}
.mini-input:focus {
  border-color: #3b82f6;
  outline: none;
}
.label-input {
  background: #f9fafb;
}
.mini-var-input {
  font-family: "Menlo", "Monaco", "Courier New", monospace;
  font-size: 11px;
}
.mini-del-btn {
  width: 24px;
  height: 24px;
  border: 1px solid #fee2e2;
  background: #fef2f2;
  color: #ef4444;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}
.mini-del-btn:hover {
  background: #fee2e2;
  border-color: #ef4444;
}
.mini-add-btn {
  width: 100%;
  padding: 8px;
  border: 1px dashed #d1d5db;
  background: #f9fafb;
  font-size: 12px;
  cursor: pointer;
  border-radius: 4px;
  color: #6b7280;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}
.mini-add-btn:hover {
  border-color: #9ca3af;
  background: #f3f4f6;
}

/* === SIGNATURE === */
.signatures-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
}
.signature-item {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  background: #f9fafb;
  transition: border-color 0.2s;
}
.signature-item:hover {
  border-color: #d1d5db;
}
.sig-inputs {
  margin-bottom: 12px;
}
.sig-field-group {
  margin-bottom: 12px;
}
.sig-field-group:last-child {
  margin-bottom: 0;
}
.sig-remove {
  color: #ef4444;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 12px;
  padding: 4px 0;
  width: 100%;
  text-align: right;
  transition: color 0.2s;
}
.sig-remove:hover {
  color: #dc2626;
  text-decoration: underline;
}
.signature-add-card {
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  min-height: 140px;
  color: #6b7280;
  background: #f9fafb;
  transition: all 0.2s;
  padding: 20px;
  gap: 8px;
}
.signature-add-card:hover {
  border-color: #9ca3af;
  background: #f3f4f6;
}
.plus-icon {
  font-size: 24px;
  font-weight: 300;
}

/* === UTILS === */
.w-full {
  width: 100%;
}
.mb-1 {
  margin-bottom: 4px;
}
.mb-2 {
  margin-bottom: 8px;
}
.mb-3 {
  margin-bottom: 12px;
}
.mb-4 {
  margin-bottom: 16px;
}
.mt-3 {
  margin-top: 12px;
}
.text-xs {
  font-size: 12px;
}
.h-9 {
  height: 36px;
}
.w-20 {
  width: 120px;
}
.text-center {
  text-align: center;
}

.error-banner {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 6px;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #9ca3af;
  border: 2px dashed #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
}
.empty-icon {
  font-size: 40px;
  margin-bottom: 12px;
  opacity: 0.6;
}
.empty-state p {
  font-size: 14px;
  margin: 0;
}
</style>
